import React, { useState } from 'react';
import Calendar from 'react-calendar';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Modal,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import 'react-calendar/dist/Calendar.css';
import './Calendar.css';

const getISTDate = () => {
  const localDate = new Date();
  const utc = localDate.getTime() + localDate.getTimezoneOffset() * 60000;
  const istOffset = 5.5 * 60 * 60000;
  return new Date(utc + istOffset);
};

const currentDate = getISTDate();

const CustomCalendar = ({ reminders = [] }) => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [open, setOpen] = useState(false);

  const isValidDate = (date) => {
    const d = new Date(date);
    return d instanceof Date && !isNaN(d.getTime());
  };

  const formatDate = (date) => {
    if (!isValidDate(date)) return null;
    return date.toISOString().split('T')[0];
  };

  const parseReminderDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (!isValidDate(date)) return null;
    return date;
  };

  const getReminderDate = (item) => {
    return parseReminderDate(item.due_date || item.period_to || item.emi_date || item.payment_due_date);
  };

  const getEventsForDate = (date) => {
    const dateStr = formatDate(date);
    if (!dateStr) return [];
    return reminders.filter((item) => {
      const reminderDate = getReminderDate(item);
      if (!reminderDate) return false;
      return formatDate(reminderDate) === dateStr;
    });
  };

  const tileClassName = ({ date, view }) => {
    if (view === 'month') {
      const dateStr = formatDate(date);
      const todayStr = formatDate(currentDate);

      const hasReminders = reminders.some((item) => {
        const reminderDate = getReminderDate(item);
        if (!reminderDate) return false;
        return formatDate(reminderDate) === dateStr;
      });

      if (dateStr === todayStr) return 'highlight-today';
      if (hasReminders) return 'highlight';
    }
    return null;
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setOpen(true);
  };

  const handleEventClick = (item) => {
    let route;
    let tabIndex;

    if (item.due_date) {
      route = `/view-all/utilities/${item.id}`;
      tabIndex = 3;
    } else if (item.period_to) {
      route = `/view-all/insurance/${item.id}`;
      tabIndex = 0;
    } else if (item.emi_date) {
      route = `/view-all/loans/${item.id}`;
      tabIndex = 1;
    } else if (item.payment_due_date) {
      route = `/view-all/rents/${item.id}`;
      tabIndex = 2;
    }

    if (route) {
      navigate(route, { state: { tab: tabIndex } });
      setOpen(false);
    }
  };

  const formatReminderMessage = (item) => {
    const reminderDate = getReminderDate(item);
    if (!reminderDate) return `Reminder #${item.id || 'N/A'} (Invalid Date)`;

    const dueDate = new Date(reminderDate);
    const today = new Date(currentDate);
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

    if (item.due_date) {
      return `${item.company || 'Utility Bill'} #${item.account_no || 'N/A'} due in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    } else if (item.period_to) {
      return `${item.insurance_company || 'Insurance'} expiring in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    } else if (item.emi_date) {
      return `${item.company_name || 'Loan'} #${item.loan_account_no || 'N/A'} EMI due in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    } else if (item.payment_due_date) {
      return `${item.company || 'Rent'} #${item.id || 'N/A'} payment due in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    }
    return `Reminder #${item.id || 'N/A'} due in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  };

  const handleViewAll = () => {
    navigate('/view-all', { state: { tab: 0 } });
    setOpen(false);
  };

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: '1rem',
        width: '100%',
        maxWidth: 458,
        height:'400px',
      background: '#121212',
color: '#fff',
    height: '400px',
        fontFamily: 'Segoe UI, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <NotificationsActiveIcon color="error" />
        <Typography variant="h6" fontWeight={600}>
          Calendar
        </Typography>
      </Box>

      <Box flexGrow={1} sx={{ width: '100%', px: 1 }}>
        <Calendar
          locale="en-US" // ✅ Starts week on Sunday
          onClickDay={handleDateClick}
          tileClassName={tileClassName}
          className="custom-calendar"
          value={currentDate}
          defaultActiveStartDate={currentDate}
          showWeekNumbers={false}
          showNeighboringMonth={false}
        />
      </Box>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 300,
            bgcolor: '#fff',
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            color: '#000',
            maxHeight: '80vh',
            overflowY: 'auto',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={600}>
              Events on {selectedDate?.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
            </Typography>
            <IconButton onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <List>
            {selectedDate && getEventsForDate(selectedDate).length > 0 ? (
              getEventsForDate(selectedDate).map((item, index) => (
                <ListItem
                  key={`reminder-${index}`}
                  onClick={() => handleEventClick(item)}
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#eee' } }}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <NotificationsActiveIcon color="error" fontSize="small" />
                        <Typography variant="body2">{formatReminderMessage(item)}</Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText primary="No reminders" />
              </ListItem>
            )}
          </List>
          <Button onClick={handleViewAll} variant="outlined" sx={{ mt: 2 }}>
            View All
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default CustomCalendar;
